import { HANGUP_MENU_VIEWPORT_RIGHT_OFFSET } from '../../meeting-ui/constants';
const getLeftAlignedStyle = (bounds) => {
    return {
        position: 'fixed',
        right: `${window.innerWidth - bounds.x}px`
    };
};
const getRightAlignedStyle = (bounds) => {
    return {
        position: 'fixed',
        left: `${bounds.x + bounds.width}px`
    };
};
const getTopAlignedStyle = (bounds) => {
    return {
        position: 'fixed',
        bottom: `${window.innerHeight - bounds.y}px`
    };
};
const getBottomAlignedStyle = (bounds) => {
    return {
        position: 'fixed',
        top: `${bounds.y + bounds.height}px`
    };
};
const getLeftRightStartAlign = (bounds, size) => {
    return {
        top: `${Math.min(bounds.y, window.innerHeight - size.height - 20)}px`
    };
};
const getLeftRightMidAlign = (bounds, size) => {
    return {
        bottom: `${window.innerHeight - bounds.y - (bounds.height / 2) - (size.height / 2)}px`
    };
};
const getLeftRightEndAlign = (bounds, size) => {
    return {
        bottom: `${Math.min(window.innerHeight - bounds.y - bounds.height, window.innerHeight - size.height)}px`
    };
};
const getTopBotMidAlign = (bounds, size) => {
    const idealRight = window.innerWidth - bounds.x - (bounds.width / 2) - (size.width / 2);
    // 贴边触发时强制留边距，避免菜单超出视口右侧
    const right = Math.max(16, Math.min(idealRight, window.innerWidth - size.width - 16));
    return {
        left: 'auto',
        right: `${right}px`
    };
};
const getTopBotStartAlign = (bounds) => {
    // 菜单右缘对齐触发器左缘附近，并保证距视口右缘至少 16px
    const idealRight = window.innerWidth - bounds.x - 6;
    const right = Math.max(16, idealRight);
    return {
        left: 'auto',
        right: `${right}px`
    };
};
const getTopBotEndAlign = (bounds) => {
    return {
        left: `${bounds.x + bounds.width - 6}px`,
        right: 'auto'
    };
};
/**
 * 挂断等贴右边菜单：右缘固定贴齐视口右缘，仅垂直方向跟触发器对齐。
 *
 * @param {number} rightOffset - 距视口右缘的间距（px）
 * @param {number} overlayRight - 视口右端被右侧侧栏占用的宽度（px），用于把弹窗右缘
 *                                退让到会议主区域右边界，避免被侧栏遮住。侧栏关闭时为 0。
 * @returns {{ left: string; right: string; }}
 */
const getTopBotViewportEndAlign = (rightOffset, overlayRight = 0) => {
    return {
        left: 'auto',
        right: `${rightOffset + overlayRight}px`
    };
};
/**
 * 挂断菜单：右缘对齐触发器右缘（相对触发器，不跟视口/侧栏宽度耦合）。
 * 侧栏打开时触发器已在会议区右缘，弹层自然落在会议区内，不会贴到侧栏或整页最右。
 *
 * @param {DOMRect} bounds - 触发器边界
 * @returns {{ left: string; right: string; }}
 */
const getTopBotTriggerEndAlign = (bounds) => {
    const right = Math.max(0, window.innerWidth - bounds.x - bounds.width);
    return {
        left: 'auto',
        right: `${right}px`
    };
};
/**
 * Gets the trigger element's and the context menu's bounds/size info and
 * computes the style to apply to the context menu to positioning it correctly
 * in regards to the given position info.
 *
 * @param {DOMRect} triggerBounds -The bounds info of the trigger html element.
 * @param {DOMRectReadOnly} dialogSize - The size info of the context menu.
 * @param {string} position - The position of the context menu in regards to the trigger element.
 * @param {number} [overlayRight] - 视口右端被侧栏占用的宽度（px），仅对 top-viewport-end
 *                                 生效；其它 position 分支会被忽略。
 *
 * @returns {Object} = The style to apply to context menu for positioning it correctly.
 */
export const getContextMenuStyle = (triggerBounds, dialogSize, position, overlayRight = 0) => {
    const parsed = position.split('-');
    switch (parsed[0]) {
        case 'top': {
            let alignmentStyle = {};
            // top-trigger-end：右缘对齐触发器右缘（挂断菜单位于按钮上方）
            if (parsed[1] === 'trigger' && parsed[2] === 'end') {
                alignmentStyle = getTopBotTriggerEndAlign(triggerBounds);
                // top-viewport-end：右缘贴视口右边（兼容旧路径；侧栏打开时需 overlayRight）
            }
            else if (parsed[1] === 'viewport' && parsed[2] === 'end') {
                alignmentStyle = getTopBotViewportEndAlign(HANGUP_MENU_VIEWPORT_RIGHT_OFFSET, overlayRight);
            }
            else if (parsed[1]) {
                alignmentStyle = parsed[1] === 'start'
                    ? getTopBotStartAlign(triggerBounds)
                    : getTopBotEndAlign(triggerBounds);
            }
            else {
                alignmentStyle = getTopBotMidAlign(triggerBounds, dialogSize);
            }
            return {
                ...getTopAlignedStyle(triggerBounds),
                ...alignmentStyle
            };
        }
        case 'bottom': {
            let alignmentStyle = {};
            // bottom-trigger-end：右缘对齐触发器右缘（挂断菜单位于顶栏按钮下方）
            if (parsed[1] === 'trigger' && parsed[2] === 'end') {
                alignmentStyle = getTopBotTriggerEndAlign(triggerBounds);
            }
            else if (parsed[1]) {
                alignmentStyle = parsed[1] === 'start'
                    ? getTopBotStartAlign(triggerBounds)
                    : getTopBotEndAlign(triggerBounds);
            }
            else {
                alignmentStyle = getTopBotMidAlign(triggerBounds, dialogSize);
            }
            return {
                ...getBottomAlignedStyle(triggerBounds),
                ...alignmentStyle
            };
        }
        case 'left': {
            let alignmentStyle = {};
            if (parsed[1]) {
                alignmentStyle = parsed[1] === 'start'
                    ? getLeftRightStartAlign(triggerBounds, dialogSize)
                    : getLeftRightEndAlign(triggerBounds, dialogSize);
            }
            else {
                alignmentStyle = getLeftRightMidAlign(triggerBounds, dialogSize);
            }
            return {
                ...getLeftAlignedStyle(triggerBounds),
                ...alignmentStyle
            };
        }
        case 'right': {
            let alignmentStyle = {};
            if (parsed[1]) {
                alignmentStyle = parsed[1] === 'start'
                    ? getLeftRightStartAlign(triggerBounds, dialogSize)
                    : getLeftRightEndAlign(triggerBounds, dialogSize);
            }
            else {
                alignmentStyle = getLeftRightMidAlign(triggerBounds, dialogSize);
            }
            return {
                ...getRightAlignedStyle(triggerBounds),
                ...alignmentStyle
            };
        }
        default: {
            return {
                ...getLeftAlignedStyle(triggerBounds),
                ...getLeftRightEndAlign(triggerBounds, dialogSize)
            };
        }
    }
};
